import { useCallback, useState } from 'react';
import { Button, Card, EmptyState, PageHeader, Spinner, StatusPill } from '../components/ui';
import { Pagination } from '../components/Pagination';
import { ConfirmDialog } from '../components/dialogs/ConfirmDialog';
import { ModerationAPI } from '../services/endpoints/admin';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { useDispatchAction } from '../hooks/useDispatchAction';
import type { Comment, Page } from '../types/api';
import { relativeTime } from '../utils/format';
import { NotImplementedBanner } from './UsersPage';
import './common.css';
import './ModerationPage.css';

type CStatus = 'FLAGGED' | 'REMOVED' | 'OK';

export function ModerationPage() {
  const [tab, setTab] = useState<CStatus>('FLAGGED');
  const [acting, setActing] = useState<{ comment: Comment; action: 'approve' | 'remove' | 'restore' } | null>(null);

  const fetcher = useCallback(async ({ page, filters }: { page: number; filters: { status: CStatus } }): Promise<Page<Comment>> => {
    return ModerationAPI.comments(filters.status, page);
  }, []);
  const q = usePagedQuery<Comment, { status: CStatus }>({ fetcher, initialFilters: { status: 'FLAGGED' } });

  function changeTab(next: CStatus) {
    setTab(next);
    q.setFilters({ status: next });
  }

  const { run } = useDispatchAction();
  async function performAction(reason?: string) {
    if (!acting) return;
    await run(`${acting.action} comment`, () => ModerationAPI.act(acting.comment.id, acting.action, reason || 'Admin action'));
    setActing(null);
    q.refresh();
  }

  return (
    <div>
      <PageHeader
        eyebrow="06 — MODERATION"
        title={<>The <em>review queue</em>.</>}
        lede="Comments that listeners flagged, or that have been removed. Approve clears the flag; remove hides from the app; restore brings back a removed comment."
      />

      <div className="nb-tabs">
        {(['FLAGGED', 'REMOVED', 'OK'] as CStatus[]).map(s => (
          <button key={s} onClick={() => changeTab(s)} className={`nb-tabs__btn ${s === tab ? 'is-active' : ''}`}>{s}</button>
        ))}
      </div>

      {q.error && <NotImplementedBanner endpoint="/api/admin/moderation/comments" />}

      {q.loading && !q.data.length ? <Card><Spinner /> Loading…</Card> :
       q.data.length === 0 ? <Card><EmptyState title="Nothing to review" hint={tab === 'FLAGGED' ? 'No flagged comments. The room is quiet.' : `No ${tab.toLowerCase()} comments.`} /></Card> :
        <Card>
          <ul className="nb-mod-list">
            {q.data.map(c => (
              <li key={c.id} className="nb-mod-item">
                <div className="nb-mod-item__head">
                  <StatusPill tone={c.status === 'FLAGGED' ? 'ember' : c.status === 'REMOVED' ? 'rust' : 'moss'}>
                    {c.status}{c.flagCount ? ` · ${c.flagCount} flags` : ''}
                  </StatusPill>
                  <span className="nb-mod-item__meta">{relativeTime(c.createdOn)}</span>
                  <span className="nb-mod-item__attrib">
                    {c.userName || c.userEmail || c.userId} · on {c.discussionId || '—'}
                  </span>
                </div>
                <p className="nb-mod-item__quote">“{c.content}”</p>
                <div className="nb-mod-item__actions">
                  {c.status === 'FLAGGED' && (
                    <>
                      <Button size="sm" tone="ember" onClick={() => setActing({ comment: c, action: 'approve' })}>Approve</Button>
                      <Button size="sm" tone="danger" onClick={() => setActing({ comment: c, action: 'remove' })}>Remove</Button>
                    </>
                  )}
                  {c.status === 'REMOVED' && (
                    <Button size="sm" tone="ember" onClick={() => setActing({ comment: c, action: 'restore' })}>Restore</Button>
                  )}
                  {c.status === 'OK' && (
                    <Button size="sm" tone="danger" onClick={() => setActing({ comment: c, action: 'remove' })}>Remove</Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <Pagination page={q.page} pageSize={q.pageSize} total={q.totalCount} onPage={q.setPage} />
        </Card>
      }

      <ConfirmDialog
        open={!!acting}
        title={
          acting?.action === 'approve' ? 'Approve comment' :
          acting?.action === 'remove'  ? 'Remove comment' :
                                         'Restore comment'
        }
        message={
          acting?.action === 'approve' ? 'Approving clears the flag and lets the comment continue to be visible.' :
          acting?.action === 'remove'  ? 'Removing hides the comment from the mobile app immediately.' :
                                         'Restoring brings the comment back into the app.'
        }
        requireReason
        destructive={acting?.action === 'remove'}
        confirmLabel={acting?.action ? acting.action[0].toUpperCase() + acting.action.slice(1) : 'Confirm'}
        onClose={() => setActing(null)}
        onConfirm={performAction}
      />
    </div>
  );
}
